import { useState, useRef, useEffect } from "react";
import styles from "./Info.module.scss";
import Input from "../../components/common/input/Input";
import Textarea from "../../components/common/textarea/Textarea";
import Button from "../../components/common/button/Button";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import AuthService from "../../api/services/auth.service";
import Select from "../../components/common/select/Select";
import { toast } from "sonner";
import Loading from "../../components/common/loading/Loading";
import { useTranslation } from "react-i18next";
import { setCookie } from "../../utils/cookies";

const Info = () => {
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [originalStoreInfo, setOriginalStoreInfo] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [storeInfo, setStoreInfo] = useState({
    name: "",
    contact_phone: "",
    email: "",
    business_address: "",
    description: "",
    business_number: "",
    store_name: "",
    store_category: "",
    preferred_language: "",
  });
  const { t, i18n } = useTranslation("translation");

  const languages = [
    { id: "en", name: t("languages.en") },
    { id: "az", name: t("languages.az") },
    { id: "de", name: t("languages.de") },
    { id: "es", name: t("languages.es") },
  ];

  useEffect(() => {
    setLoading(true);
    const getStoreData = async () => {
      try {
        const res = await AuthService.getStoreInfo();
        setStoreInfo(res.data);
        setOriginalStoreInfo(res.data);
        if (res.data.logo_url) setLogo(res.data.logo_url);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    getStoreData();
  }, []);

  useEffect(() => {
    if (!originalStoreInfo) return;

    const isDifferent =
      JSON.stringify({
        ...storeInfo,
        logo,
      }) !==
      JSON.stringify({
        ...originalStoreInfo,
        logo: originalStoreInfo.logo_url || null,
      });

    setHasChanges(isDifferent);
  }, [storeInfo, logo, originalStoreInfo]);

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectChange = (option: { id: string; name: string } | null) => {
    if (option) {
      setStoreInfo((prev) => ({ ...prev, preferred_language: option.id }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setStoreInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    setAddLoading(true);
    try {
      const formData = new FormData();
      Object.entries(storeInfo).forEach(([key, value]) => {
        if (value !== originalStoreInfo?.[key]) {
          formData.append(key, value || "");
        }
      });
      if (logoFile) {
        formData.append("logo_url", logoFile);
      }
      await AuthService.updateStoreInfo(formData);
      toast.success(t("store_info_messages.success"));
      setOriginalStoreInfo({ ...storeInfo, logo_url: logo });
      setCookie("lux_store_lang", storeInfo.preferred_language, 365);
      await i18n.changeLanguage(storeInfo.preferred_language);
      setLogoFile(null);
      setHasChanges(false);
    } catch (err) {
      toast.error(t("store_info_messages.error"));
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.store_info}>
      <TitleUpdater title={t("store_info")} />
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.add_logo}>
          <div>
            <p className={styles.label}>{t("store_logo")}</p>
            <div className={styles.store_logo} onClick={handleLogoClick}>
              {logo ? (
                <img src={logo} alt="Store" />
              ) : (
                <div className={styles.empty_logo}>{t("logo_placeholder")}</div>
              )}
            </div>
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleLogoChange}
        />
        <div className={styles.inputs}>
          <div className={styles.input}>
            <Input
              label={t("labels.store_name") || ""}
              name="name"
              placeholder={t("placeholders.store_name") || ""}
              value={storeInfo.name}
              onChange={handleChange}
            />
          </div>
          <div className={styles.input}>
            <Textarea
              label={t("labels.description") || ""}
              name="description"
              placeholder={t("placeholders.description") || ""}
              value={storeInfo.description}
              onChange={handleChange}
              maxLength={500}
              showCount={true}
            />
          </div>
          <div className={styles.input}>
            <Input
              label={t("labels.phone") || ""}
              name="contact_phone"
              placeholder={t("placeholders.phone") || ""}
              value={storeInfo.contact_phone}
              onChange={handleChange}
            />
          </div>
          <div className={styles.input}>
            <Input
              label={t("labels.email") || ""}
              name="email"
              placeholder={t("placeholders.email") || ""}
              value={storeInfo.email}
              onChange={handleChange}
              disabled
            />
          </div>
          <div className={styles.input}>
            <Input
              label={t("labels.business_address") || ""}
              name="business_address"
              placeholder={t("placeholders.business_address") || ""}
              value={storeInfo.business_address}
              onChange={handleChange}
              disabled
            />
          </div>
          <div className={styles.input}>
            <Select
              label={t("labels.preferred_language") || ""}
              value={storeInfo.preferred_language}
              options={languages}
              onChange={handleSelectChange}
            />
          </div>
        </div>

        <div className={styles.submit}>
          <Button type="submit" disabled={!hasChanges || addLoading}>
            {t("submit")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Info;
